import isNil from "lodash/isNil";
import { Avatar as BaseAvatar } from "tamagui";

import { useGetAvatarQuery } from "@/store/api/user";
import type { TypographyVariant } from "@/ui/core/Text";
import Text from "@/ui/core/Text";
import Box from "@/ui/layout/Box";
import { ColorToken } from "@/ui/theme/tokens";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { joinTestIDs } from "@/utils/string.util";
import { getInitialsFromName } from "@/utils/user.util";

type AvatarSize = "md" | "sm" | "xs" | "xxs";

interface AvatarProps extends BaseViewProps {
  imageUrl?: string;
  size?: AvatarSize;
  userId?: string;
  userName: string;
  userType: BaseUserType;
}

const AvatarUserTypeValues: Record<BaseUserType, BaseUserType> = {
  patient: "patient",
  provider: "provider",
  support: "support",
};

const getFallbackColor = (userType: BaseUserType) => {
  switch (userType) {
    case "patient":
      return ColorToken.TertiaryMarigoldBackground;
    case "provider":
      return ColorToken.TertiaryOrangeBackground;
    case "support":
      return ColorToken.TertiaryMarigoldBackground;
  }
};

interface AvatarStyleProps {
  size: number;
  textVariant: TypographyVariant;
}

const AvatarStyleMapper: Record<AvatarSize, AvatarStyleProps> = {
  md: {
    size: 48,
    textVariant: "body",
  },
  sm: {
    size: 40,
    textVariant: "bodyMedium",
  },
  xs: {
    size: 32,
    textVariant: "captionMedium",
  },
  xxs: {
    size: 24,
    textVariant: "overlineMedium",
  },
};

const Avatar = ({
  imageUrl,
  size = "sm",
  testID,
  userId,
  userName,
  userType,
}: AvatarProps) => {
  const backgroundColor = getFallbackColor(userType);
  const baseTestID = joinTestIDs(testID, "avatar");
  const avatarStyles = AvatarStyleMapper[size];

  // Only fetch avatar for providers, not patients or support users
  const shouldSkipAvatar =
    userType !== "provider" || !isNotNilNotEmpty(userId) || !isNil(imageUrl);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- userId is required but only for providers
  const { data: fetchedAvatarUrl } = useGetAvatarQuery(userId!, {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- skip naming is defined by RTKQuery
    skip: shouldSkipAvatar,
  });

  const initials = getInitialsFromName(userName);
  // Prefer passed imageUrl over fetchedAvatarUrl
  const avatarImageUrl = imageUrl || fetchedAvatarUrl;

  return (
    <Box testID={baseTestID}>
      <BaseAvatar
        alignItems="center"
        circular
        justifyContent="center"
        size={avatarStyles.size}
      >
        <BaseAvatar.Image
          src={avatarImageUrl}
          testID={joinTestIDs(baseTestID, "image")}
        />
        <BaseAvatar.Fallback
          alignItems="center"
          backgroundColor={backgroundColor}
          justifyContent="center"
        >
          <Text
            color={ColorToken.Primary90Variant}
            testID={joinTestIDs(baseTestID, "fallback")}
            variant={avatarStyles.textVariant}
          >
            {initials}
          </Text>
        </BaseAvatar.Fallback>
      </BaseAvatar>
    </Box>
  );
};

export default Avatar;

export { AvatarStyleMapper, AvatarUserTypeValues };
export type { AvatarProps, AvatarSize };
